"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { allQueryKeys } from "#app/_api/allQueryKeys";
import { logError } from "#app/lib/logger";

export const defaultDisabledMessage = "Registration is currently disabled.";

/**
 * Handles registration errors, including outages and downstream mutation errors.
 */
export function useRegistrationErrors() {
  // Fetch outage status
  const { data, error, isError, isPending, isSuccess } = useQuery(
    allQueryKeys.externalBolRegistrationService.registrationOutage,
  );

  // State for manually catching errors
  const [disabledMessage, setDisabledMessage] = useState<string | null>(null);

  // Log query errors
  if (isError) {
    logError(error);
  }

  // Compute error state with clear precedence
  const errorState = useMemo(() => {
    // Priority 1: Query failed (network/server error)
    if (isError) {
      return {
        error: defaultDisabledMessage,
        isError: true,
        type: "query-error" as const,
      };
    }

    // Priority 2: Query succeeded (not-allowed response)
    if (isSuccess && data.isAllowed === false) {
      return {
        error: data.message || defaultDisabledMessage,
        isError: true,
        type: "not-allowed" as const,
      };
    }

    // Priority 3: Mutation error (caught/manually disabled)
    if (disabledMessage) {
      return {
        error: disabledMessage,
        isError: true,
        type: "mutation-error" as const,
      };
    }

    // No errors
    return {
      error: null,
      isError: false,
      type: null,
    };
  }, [isError, isSuccess, data, disabledMessage]);

  return {
    error: errorState.error,
    errorType: errorState.type,
    isError: errorState.isError,
    isPending,
    isSuccess,
    defaultDisabledMessage,
    setDisabledMessage,
  };
}
